/* Mialica event pages (first use: PharmEvolution 2026, 14 Oct 2026).
   Loaded by the mialica-event-* sections. Every section includes this file, so it guards against running twice.
   - sticky offset for the in-page navigation (height of the theme's sticky header bar)
   - reveal on scroll (only hides what is off screen, and only once this script runs)
   - in-page navigation: hides links to sections that are not on the page, highlights the section in view
   - in-page buttons (such as #galleria): hidden on the live page while their section is not on the page
   - moments: arrow buttons
   - gallery: filters and full-screen photo viewer (empty "coming soon" frames are not opened)
   - videos: player window (Shopify video, YouTube or Vimeo), loaded only when a video is opened
   Works again after the theme editor re-renders a section. */
(function () {
  'use strict';

  if (window.MialicaEvent) return;

  var doc = document;
  var root = doc.documentElement;
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var hasIO = 'IntersectionObserver' in window;

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function smooth() {
    return reduce ? 'auto' : 'smooth';
  }

  /* 1. Sticky offset: the in-page navigation sits right under the theme's sticky header bar */
  function headerBar() {
    return doc.querySelector('.mialica-header .mh-main');
  }

  function setStickyTop() {
    var bar = headerBar();
    var height = bar && bar.classList.contains('mh-sticky') ? bar.offsetHeight : 0;
    root.style.setProperty('--mev-sticky-top', height + 'px');
  }

  function watchHeader() {
    setStickyTop();
    var bar = headerBar();
    if (bar && 'ResizeObserver' in window) {
      new ResizeObserver(setStickyTop).observe(bar);
    } else {
      window.addEventListener('resize', setStickyTop);
    }
  }

  /* 2. Reveal on scroll */
  var revealer = null;

  function reveal(scope) {
    var items = (scope || doc).querySelectorAll('.mev .mev-rv:not(.is-in)');
    if (!items.length) return;

    if (designMode || reduce || !hasIO) {
      each(items, function (el) { el.classList.add('is-in'); });
      return;
    }

    // What is already on screen is shown straight away, so nothing flashes.
    var viewport = window.innerHeight || root.clientHeight;
    each(items, function (el) {
      var box = el.getBoundingClientRect();
      if (box.bottom > 0 && box.top < viewport && (box.width || box.height)) el.classList.add('is-in');
    });

    if (!revealer) {
      revealer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            revealer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    }

    each(items, function (el) {
      if (!el.classList.contains('is-in')) revealer.observe(el);
    });

    if (!root.classList.contains('mev-ready')) {
      root.classList.add('mev-ready');
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () { root.classList.add('mev-anim'); });
      });
    }
  }

  /* 3. In-page navigation */
  function idOf(link) {
    var id = link.getAttribute('href').slice(1);
    try {
      return decodeURIComponent(id);
    } catch (error) {
      return id;
    }
  }

  function initNav(nav) {
    if (nav.hasAttribute('data-mev-ready')) return;
    nav.setAttribute('data-mev-ready', '');

    var strip = nav.querySelector('.mev-nav__in');
    var links = nav.querySelectorAll('a[href^="#"]');
    var byId = {};
    var targets = [];

    each(links, function (link) {
      var id = idOf(link);
      var target = id ? doc.getElementById(id) : null;
      // On the live page, a link whose section is missing or empty is hidden.
      if (!target) {
        if (!designMode) link.hidden = true;
        return;
      }
      link.hidden = false;
      byId[id] = link;
      targets.push(target);
    });

    var visible = Array.prototype.filter.call(links, function (link) { return !link.hidden; });
    var section = nav.closest('.shopify-section') || nav;
    if (!designMode) section.style.display = visible.length ? '' : 'none';

    if (!hasIO || !targets.length) return;

    var spy = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var link = byId[entry.target.id];
        if (!link) return;
        each(links, function (l) { l.removeAttribute('aria-current'); });
        link.setAttribute('aria-current', 'true');
        if (strip && strip.scrollWidth > strip.clientWidth) {
          strip.scrollTo({ left: link.offsetLeft - 16, behavior: smooth() });
        }
      });
    }, { rootMargin: '-40% 0px -55% 0px' });

    targets.forEach(function (target) { spy.observe(target); });
  }

  /* In-page buttons outside the navigation (for example "View the gallery" pointing to #galleria).
     On the live page a button whose section is not on the page (no photos yet) is hidden,
     the next button in the row takes the main button's look, and an empty row is hidden.
     The theme editor shows them all. */
  function checkLinks() {
    if (designMode) return;
    each(doc.querySelectorAll('.mev a[href^="#"]'), function (link) {
      if (link.closest('[data-mev-nav]')) return;
      var id = idOf(link);
      link.style.display = id && doc.getElementById(id) ? '' : 'none';
    });
    each(doc.querySelectorAll('.mev .mev-actions'), function (row) {
      var shown = Array.prototype.filter.call(row.children, function (child) { return child.style.display !== 'none'; });
      row.style.display = shown.length ? '' : 'none';
      var main = row.querySelector('.mev-btn:not(.mev-btn--ghost)');
      if (main && main.style.display === 'none' && shown.length) shown[0].classList.remove('mev-btn--ghost');
    });
  }

  /* 4. Moments: arrow buttons */
  function initMoments(section) {
    if (section.hasAttribute('data-mev-ready')) return;
    section.setAttribute('data-mev-ready', '');

    var track = section.querySelector('[data-mev-track]');
    if (!track) return;
    each(section.querySelectorAll('[data-mev-step]'), function (button) {
      button.addEventListener('click', function () {
        var card = track.querySelector('.mev-moment');
        var gap = parseFloat(window.getComputedStyle(track).columnGap) || 24;
        var step = card ? card.getBoundingClientRect().width + gap : 320;
        track.scrollBy({ left: button.getAttribute('data-mev-step') === 'next' ? step : -step, behavior: smooth() });
      });
    });
  }

  /* 5. Dialogs (photo viewer, video window) */
  function makeDialog(el) {
    // Moved to the end of <body> so no parent element can clip or cover it.
    if (el.parentNode !== doc.body) doc.body.appendChild(el);

    var stage = el.querySelector('.mev-dialog__stage');
    var cap = el.querySelector('.mev-dialog__cap');
    var count = el.querySelector('.mev-dialog__count');
    var lastFocus = null;
    var closeTimer = null;

    function focusable() {
      return Array.prototype.filter.call(el.querySelectorAll('button, [href], iframe, video[controls], [tabindex]:not([tabindex="-1"])'), function (node) {
        return !node.hidden && node.offsetParent !== null;
      });
    }

    function open(trigger) {
      window.clearTimeout(closeTimer);
      lastFocus = trigger || null;
      el.hidden = false;
      doc.body.classList.add('mev-lock');
      window.requestAnimationFrame(function () { el.classList.add('is-open'); });
      var closer = el.querySelector('[data-mev-close]');
      if (closer) closer.focus();
    }

    function close() {
      if (el.hidden) return;
      el.classList.remove('is-open');
      doc.body.classList.remove('mev-lock');
      closeTimer = window.setTimeout(function () {
        el.hidden = true;
        stage.innerHTML = '';
      }, reduce ? 0 : 250);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    el.addEventListener('click', function (event) {
      if (event.target === el || event.target.closest('[data-mev-close]')) close();
    });

    el.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key === 'Tab') {
        var items = focusable();
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        if (event.shiftKey && doc.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && doc.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    return { el: el, stage: stage, cap: cap, count: count, open: open, close: close };
  }

  function ratioNumber(value) {
    var parts = String(value || '4/3').split('/');
    var w = parseFloat(parts[0]);
    var h = parseFloat(parts[1]);
    if (!h) return w || 1.333;
    return (w || 4) / h;
  }

  /* 6. Gallery: filters and photo viewer */
  function initGallery(section) {
    if (section.hasAttribute('data-mev-ready')) return;
    section.setAttribute('data-mev-ready', '');

    var tiles = Array.prototype.slice.call(section.querySelectorAll('.mev-g'));
    var filters = section.querySelectorAll('[data-mev-filter]');

    function setFilter(value) {
      each(filters, function (button) {
        button.setAttribute('aria-pressed', button.getAttribute('data-mev-filter') === value ? 'true' : 'false');
      });
      tiles.forEach(function (tile) {
        tile.hidden = !(value === 'all' || tile.getAttribute('data-cat') === value);
        if (!tile.hidden) tile.classList.add('is-in');
      });
    }

    each(filters, function (button) {
      button.addEventListener('click', function () { setFilter(button.getAttribute('data-mev-filter')); });
    });
    section._mevSetFilter = setFilter;

    var box = section.querySelector('[data-mev-lightbox]');
    if (!box) return;
    var viewer = makeDialog(box);
    var current = 0;

    // Only photo buttons open the viewer; empty "coming soon" frames on the live page are not buttons.
    function visibleTiles() {
      return tiles.filter(function (tile) { return !tile.hidden && tile.tagName === 'BUTTON'; });
    }

    function show(index) {
      var list = visibleTiles();
      if (!list.length) return;
      current = (index + list.length) % list.length;
      var tile = list[current];
      var media;
      var full = tile.getAttribute('data-full');
      if (full) {
        media = doc.createElement('img');
        media.src = full;
        media.alt = tile.getAttribute('data-alt') || '';
        media.decoding = 'async';
      } else {
        var slot = tile.querySelector('.mev-ph');
        media = slot ? slot.cloneNode(true) : doc.createElement('div');
        media.style.setProperty('--r-num', ratioNumber(media.style.getPropertyValue('--ratio')));
      }
      viewer.stage.innerHTML = '';
      viewer.stage.appendChild(media);
      viewer.cap.textContent = tile.getAttribute('data-caption') || '';
      viewer.count.textContent = (current + 1) + ' / ' + list.length;
      var single = list.length < 2;
      each(box.querySelectorAll('[data-mev-step]'), function (button) { button.hidden = single; });
    }

    tiles.forEach(function (tile) {
      if (tile.tagName !== 'BUTTON') return;
      tile.addEventListener('click', function () {
        show(visibleTiles().indexOf(tile));
        viewer.open(tile);
      });
    });

    each(box.querySelectorAll('[data-mev-step]'), function (button) {
      button.addEventListener('click', function () {
        show(current + parseInt(button.getAttribute('data-mev-step'), 10));
      });
    });

    box.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') show(current - 1);
      if (event.key === 'ArrowRight') show(current + 1);
    });
  }

  /* 7. Videos: player window */
  function initVideos(section) {
    if (section.hasAttribute('data-mev-ready')) return;
    section.setAttribute('data-mev-ready', '');

    var box = section.querySelector('[data-mev-player]');
    if (!box) return;
    var player = makeDialog(box);

    each(section.querySelectorAll('button.mev-v'), function (tile) {
      tile.addEventListener('click', function () {
        var template = tile.querySelector('template');
        player.stage.innerHTML = '';
        if (template) {
          player.stage.appendChild(template.content.cloneNode(true));
        } else {
          var slot = tile.querySelector('.mev-vph');
          var copy = slot ? slot.cloneNode(true) : doc.createElement('div');
          copy.style.setProperty('--r-num', ratioNumber(tile.getAttribute('data-ratio')));
          player.stage.appendChild(copy);
        }
        player.cap.textContent = tile.getAttribute('data-caption') || '';
        player.open(tile);
        var video = player.stage.querySelector('video');
        if (video && video.play) {
          var playing = video.play();
          if (playing && playing.catch) playing.catch(function () {});
        }
      });
    });
  }

  /* Start */
  function init(scope) {
    scope = scope || doc;
    each(scope.querySelectorAll('[data-mev-moments]'), initMoments);
    each(scope.querySelectorAll('[data-mev-gallery]'), initGallery);
    each(scope.querySelectorAll('[data-mev-videos]'), initVideos);
    reveal(scope);
    // The navigation and the in-page buttons look for every section on the page, so they are set up last.
    each(doc.querySelectorAll('[data-mev-nav]'), function (nav) {
      if (scope !== doc) nav.removeAttribute('data-mev-ready');
      initNav(nav);
    });
    checkLinks();
  }

  function start() {
    watchHeader();
    init(doc);
  }

  window.MialicaEvent = { init: init };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* Theme editor */
  doc.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  doc.addEventListener('shopify:section:unload', function (event) {
    var id = event.detail && event.detail.sectionId;
    if (!id) return;
    each(doc.querySelectorAll('body > .mev-dialog[data-mev-owner="' + id + '"]'), function (dialog) {
      dialog.parentNode.removeChild(dialog);
    });
    doc.body.classList.remove('mev-lock');
  });

  doc.addEventListener('shopify:block:select', function (event) {
    var block = event.target;
    if (!block || !block.closest) return;
    var gallery = block.closest('[data-mev-gallery]');
    if (gallery && gallery._mevSetFilter && block.hidden) gallery._mevSetFilter('all');
    if (block.closest('[data-mev-track]') && block.scrollIntoView) {
      block.scrollIntoView({ behavior: smooth(), block: 'nearest', inline: 'start' });
    }
  });
})();
